import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { Card } from "@/components/brand/Card";
import { cn } from "@/lib/utils";

import { guideChapters } from "./guide";

/**
 * O guia de integração: o índice dos capítulos à esquerda e o capítulo aberto à direita. O capítulo
 * vive na URL (`?chapter=`), para o link de um capítulo abrir direto nele.
 */
export function GuideView() {
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const chapters = guideChapters(i18n.language);
  const current = chapters.find((chapter) => chapter.slug === params.get("chapter")) ?? chapters[0];

  const open = (slug: string) =>
    setParams(
      (prev) => {
        const merged = new URLSearchParams(prev);
        merged.set("chapter", slug);
        return merged;
      },
      { replace: true },
    );

  if (!current) return null;

  return (
    <div className="flex flex-col lg:flex-row gap-6 items-start">
      <nav aria-label={t("Chapters")} className="w-full lg:w-64 shrink-0">
        <ol className="flex flex-col gap-1">
          {chapters.map((chapter, index) => (
            <li key={chapter.slug}>
              <button
                type="button"
                onClick={() => open(chapter.slug)}
                aria-current={chapter.slug === current.slug ? "page" : undefined}
                className={cn(
                  "w-full text-left px-3 py-2 rounded-md text-[13px] flex gap-2 transition-colors",
                  chapter.slug === current.slug
                    ? "bg-[#08254c] text-white font-semibold"
                    : "text-[#596b85] hover:bg-[#f2f4f7] hover:text-[#08254c]",
                )}
              >
                <span aria-hidden className="mono text-[11px] opacity-70 pt-px">
                  {String(index + 1).padStart(2, "0")}
                </span>
                {chapter.title}
              </button>
            </li>
          ))}
        </ol>
      </nav>

      <Card className="flex-1 min-w-0 w-full">
        <article className="px-6 py-5 max-w-4xl text-[14px] leading-relaxed text-[#08254c] break-words [&_h1]:font-brand [&_h1]:text-[24px] [&_h1]:font-bold [&_h1]:mb-4 [&_h2]:font-brand [&_h2]:text-[18px] [&_h2]:font-bold [&_h2]:mt-7 [&_h2]:mb-3 [&_h3]:font-semibold [&_h3]:mt-5 [&_h3]:mb-2 [&_p]:my-3 [&_ul]:my-3 [&_ul]:pl-5 [&_ul]:list-disc [&_ol]:my-3 [&_ol]:pl-5 [&_ol]:list-decimal [&_li]:my-1 [&_strong]:font-semibold [&_a]:underline">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              table: (props) => (
                <div className="overflow-x-auto my-4 border border-[#e8ebf0] rounded-md">
                  <table className="tbl" {...props} />
                </div>
              ),
              pre: (props) => (
                <pre
                  className="overflow-x-auto my-4 rounded-md bg-[#08254c] text-[#e8ebf0] p-4 text-[12px] leading-normal [&_code]:bg-transparent [&_code]:p-0 [&_code]:text-inherit"
                  {...props}
                />
              ),
              code: (props) => (
                <code
                  className="mono text-[12.5px] bg-[#f2f4f7] text-[#08254c] rounded px-1 py-px"
                  {...props}
                />
              ),
            }}
          >
            {current.body}
          </ReactMarkdown>
        </article>
      </Card>
    </div>
  );
}
